import _ from 'lodash';

import { GLOBAL_COLORS } from 'constants/ColorConstants';

// =============================================================================
// Constants
// =============================================================================

export const RADIAN = Math.PI / 180;

// =============================================================================
// Chart Data Utilities
// =============================================================================

export const calculateChartDataTotal = (arr) => {
  if (!arr || !Array.isArray(arr) || arr.length === 0) {
    return 0;
  }

  const excludeKeys = ['date', 'dateStr', 'dateLabel', 'id'];

  return _.sum(
    _.flatMap(arr, obj =>
      Object.entries(obj)
        .filter(([key, value]) =>
          _.isNumber(value) &&
          !key.includes('_percent') &&
          !excludeKeys.includes(key)
        )
        .map(([key, value]) => value)
    )
  );
};

// =============================================================================
// Text Utilities
// =============================================================================

export function getWrappedText(text = '', availableWidth){
    if(Number.isNaN(availableWidth)) {
        return text;
    }
    let trimmedText = text;
    let textWidth = trimmedText.length * 6;
    let count = 0;
    availableWidth = availableWidth - availableWidth % 10;
    while ( textWidth && textWidth > (availableWidth - 15) && text.length > 0) {
        trimmedText = trimmedText.slice(0, -2);
        count++;
        textWidth = trimmedText.length * 6;
    }

    // If we are trimming only 1 char, then no need to trim
    if(count === 1){
        return text;
    }
    return trimmedText + (count > 0 ? '...' : '');
}

// =============================================================================
// Geometry Utilities
// =============================================================================

export function getDistance(p1, p2){
	return Math.abs(Math.sqrt((Math.pow(p1.x-p2.x,2))+(Math.pow(p1.y-p2.y,2))));
}

export function getPoint(p, angle, rad) {
	const temp = {};

	temp.x = parseFloat((p.x + (rad * Math.cos(angle))).toFixed(5));
	temp.y = parseFloat((p.y + (rad * Math.sin(angle))).toFixed(5));

	return temp;
}

export function getAngle(p1, p2) {
	return Math.atan2(p2.y - p1.y, p2.x - p1.x);
}

// =============================================================================
// Pie Chart Label Utilities
// =============================================================================

export const getLableLinePoints = ({ points, cx, cy, index }, previous2ndPointRef, distanceToCheck, angleToAdd, point2Angle, offset = 10) => {

	const [point1] = points;
	let [, point2] = points;
	let angle = getAngle(point1, point2);
	point2 = getPoint(point2, angle, offset);
	if(previous2ndPointRef?.current){
		const center = { x: cx, y: cy };
		previous2ndPointRef.current.forEach((prevPoint, i) => {
			if(i === index){
				return;
			}
			const distance = getDistance(point2, prevPoint);
			if (distance <= distanceToCheck) {
				angle = getAngle(center, prevPoint);
				let multiplier = 1;
				if (prevPoint.y >= point2.y) {
					multiplier = -1;
				}

				const newPoint = getPoint(center, angle = angle + (angleToAdd * multiplier), getDistance(center, prevPoint));
				point2.x = newPoint.x;
				point2.y = newPoint.y;
			}
		})
	}

    if (previous2ndPointRef) {
        previous2ndPointRef.current[index] = { ...point2 };
    }
	point2 = getPoint(point2, getAngle(point1, point2) + Math.PI, point2Angle);

    return { point1, point2 };
}

export const getCustomPieLabelPoint = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, ...props }, previousYRef, radius, distanceToCheck, angleToAdd) => {
    const center = { x: cx, y: cy };
	let x = cx + radius * Math.cos(-midAngle * RADIAN);
	let y = cy + radius * Math.sin(-midAngle * RADIAN);
	if(previousYRef?.current){
		previousYRef.current.forEach((prevPoint, i) => {
			if(i === props.index){
				return;
			}
			const distance = getDistance({x, y}, prevPoint);
			if (distance <= distanceToCheck) {
				const angle = getAngle(center, prevPoint);
				let multiplier = 1;
				if (prevPoint.y >= y) {
					multiplier = -1;
				}

				const newPoint = getPoint(center, angle + (angleToAdd * multiplier), getDistance(center, prevPoint));
				x = newPoint.x;
				y = newPoint.y;
			}
		})
	}

    if (previousYRef) {
        previousYRef.current[props.index] = { x, y };
    }

	return { x, y };
};

// =============================================================================
// Color Utilities
// =============================================================================

export function stringToColour(str, index) {
	if (GLOBAL_COLORS[index]) {
		return GLOBAL_COLORS[index];
	}

	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	let colour = '#';
	for (let i = 0; i < 3; i++) {
		const value = ((hash >> (i * 8)) & 0xFF).toString(16);
		colour += ('00' + value).substring(value.length, value.length + 2);
	}
	return colour;
}
